import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';


const AccountingSettings = ({ onChange }) => {
  const [settings, setSettings] = useState({
    accountingMethod: 'accrual',
    defaultRevenueAccount: '4000',
    defaultExpenseAccount: '5000',
    defaultAssetAccount: '1000',
    defaultLiabilityAccount: '2000',
    autoReconciliation: true,
    autoCategorizationEnabled: true,
    requireApproval: true,
    allowNegativeInventory: false,
    trackInventory: true,
    multiCurrency: false,
    retainedEarningsAccount: '3900'
  });

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    onChange?.();
  };

  const accountingMethodOptions = [
    { value: 'accrual', label: 'Accrual Basis' },
    { value: 'cash', label: 'Cash Basis' }
  ];

  const accountOptions = [
    { value: '1000', label: '1000 - Cash and Bank' },
    { value: '1200', label: '1200 - Accounts Receivable' },
    { value: '2000', label: '2000 - Accounts Payable' },
    { value: '3000', label: '3000 - Equity' },
    { value: '3900', label: '3900 - Retained Earnings' },
    { value: '4000', label: '4000 - Revenue' },
    { value: '5000', label: '5000 - Operating Expenses' }
  ];

  return (
    <div className="space-y-8">
      {/* Accounting Method */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Accounting Method</h3>
        <Select
          label="Accounting Basis"
          value={settings?.accountingMethod}
          onChange={(value) => handleChange('accountingMethod', value)}
          options={accountingMethodOptions}
          description="Choose how you want to record transactions"
        />
      </div>
      {/* Default Accounts */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Default Account Assignments</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Default Revenue Account"
            value={settings?.defaultRevenueAccount}
            onChange={(value) => handleChange('defaultRevenueAccount', value)}
            options={accountOptions}
          />
          <Select
            label="Default Expense Account"
            value={settings?.defaultExpenseAccount}
            onChange={(value) => handleChange('defaultExpenseAccount', value)}
            options={accountOptions}
          />
          <Select
            label="Default Asset Account"
            value={settings?.defaultAssetAccount}
            onChange={(value) => handleChange('defaultAssetAccount', value)}
            options={accountOptions}
          />
          <Select
            label="Default Liability Account"
            value={settings?.defaultLiabilityAccount}
            onChange={(value) => handleChange('defaultLiabilityAccount', value)}
            options={accountOptions}
          />
          <Select
            label="Retained Earnings Account"
            value={settings?.retainedEarningsAccount}
            onChange={(value) => handleChange('retainedEarningsAccount', value)}
            options={accountOptions}
          />
        </div>
      </div>
      {/* Automation Rules */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Automation & Rules</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={settings?.autoReconciliation}
              onChange={(e) => handleChange('autoReconciliation', e?.target?.checked)}
            />
            <div>
              <label className="text-sm font-medium text-gray-900">Auto Bank Reconciliation</label>
              <p className="text-sm text-gray-600">Automatically match bank transactions with ledger entries</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              checked={settings?.autoCategorizationEnabled}
              onChange={(e) => handleChange('autoCategorizationEnabled', e?.target?.checked)}
            />
            <div>
              <label className="text-sm font-medium text-gray-900">Auto-Categorization</label>
              <p className="text-sm text-gray-600">Use AI to automatically categorize transactions</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              checked={settings?.requireApproval}
              onChange={(e) => handleChange('requireApproval', e?.target?.checked)}
            />
            <div>
              <label className="text-sm font-medium text-gray-900">Require Approval for Expenses</label>
              <p className="text-sm text-gray-600">All expenses must be approved before posting</p>
            </div>
          </div>
        </div>
      </div>
      {/* Inventory & Multi-Currency */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Advanced Settings</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={settings?.trackInventory}
              onChange={(e) => handleChange('trackInventory', e?.target?.checked)}
            />
            <div>
              <label className="text-sm font-medium text-gray-900">Track Inventory</label>
              <p className="text-sm text-gray-600">Enable inventory tracking and COGS calculations</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              checked={settings?.allowNegativeInventory}
              onChange={(e) => handleChange('allowNegativeInventory', e?.target?.checked)}
              disabled={!settings?.trackInventory}
            />
            <div>
              <label className="text-sm font-medium text-gray-900">Allow Negative Inventory</label>
              <p className="text-sm text-gray-600">Permit inventory quantities to go below zero</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              checked={settings?.multiCurrency}
              onChange={(e) => handleChange('multiCurrency', e?.target?.checked)}
            />
            <div>
              <label className="text-sm font-medium text-gray-900">Multi-Currency Support</label>
              <p className="text-sm text-gray-600">Enable transactions in multiple currencies</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingSettings;